import { CLOUD, loggedIn, sbRefresh, sbRest, sbUrl } from '../cloud/api.js';
import { ctx } from '../cloud/plan.js';
import { cloudPush } from '../cloud/sync.js';
import { t } from '../i18n/index.js';
import { setSchoolCtx, stu } from '../model.js';
import { S } from '../state.js';
import { IC, closeSheet, esc, render, sheet, toast } from '../ui.js';

/* Teams: a school is a workspace with several members.
   owner = the school's director (billing, can't be removed), admin = helps run the school,
   tutor = a teacher who only sees their own students (enforced in the database). */
export const TEAM={orgs:null,members:null,invites:null,terms:null,tinv:null};

export const role=()=>(ctx()&&ctx().role)||'owner';
export const isOwner=()=>role()==='owner';
export const isManager=()=>role()==='owner'||role()==='admin';
export const isTutor=()=>role()==='tutor';
/* a school: more than one person, a team plan, open invitations, or the owner said so at sign-up */
export const inTeam=()=>{const c=ctx();if(!loggedIn()||!c)return false;
  return c.members>1||(c.role&&c.role!=='owner')||['team','team_plus','enterprise'].includes(c.plan)||c.subscribed_plan==='team'
    ||!!(S.settings&&S.settings.school)||!!(TEAM.invites&&TEAM.invites.length)};
setSchoolCtx(inTeam);   // a school prices by place: in der Schule, beim Schüler, online
/* owners and admins of a school get the school-wide screens */
export const schoolView=()=>inTeam()&&isManager();
export const memberName=uid=>{const m=(TEAM.members||[]).find(x=>x.user_id===uid);return m?m.name:''};
export const memberInitials=name=>(name||'?').split(/\s+/).filter(Boolean).slice(0,2).map(w=>w[0]).join('').toUpperCase();
export const firstName=name=>String(name||'').trim().split(/\s+/)[0]||'';
const COLORS=['#2242C8','#C2472F','#217A52','#8248B8','#B8790E','#1F7A8C','#A33A6B','#4F6B2A'];
export const NO_TEACHER='#9A9CA6';
export const memberColor=uid=>{const i=(TEAM.members||[]).findIndex(x=>x.user_id===uid);return i<0?NO_TEACHER:COLORS[i%COLORS.length]};
export const isMember=uid=>!!uid&&(TEAM.members||[]).some(x=>x.user_id===uid);
/* a school's director may only run the school: then they aren't a teacher.
   S.settings.ownerTeaches: undefined = not asked yet (counts as teaching), false = runs only */
export const ownerTeaches=()=>!(S.settings&&S.settings.ownerTeaches===false);
const ownerUid=()=>((TEAM.members||[]).find(x=>x.role==='owner')||{}).user_id||null;
export const isTeacher=uid=>isMember(uid)&&(ownerTeaches()||uid!==ownerUid());
export const teachers=()=>(TEAM.members||[]).filter(m=>isTeacher(m.user_id));
/* who teaches a lesson: the lesson's own teacher, else the student's */
export const teacherOf=e=>(e&&e.tutorId)||(stu(e&&e.studentId)||{}).tutorId||null;
export const isMine=e=>{const u=CLOUD.auth&&CLOUD.auth.uid;return !!u&&teacherOf(e)===u};
/* the signed-in person's own name (in a school the workspace settings hold the director's name) */
export function myName(){
  const own=(CLOUD.auth&&CLOUD.auth.name)||'';
  if(inTeam()){const me=(TEAM.members||[]).find(x=>x.me);return (me&&me.name)||own||S.settings.teacher||''}
  return S.settings.teacher||own;
}
export const myUid=()=>(CLOUD.auth&&CLOUD.auth.uid)||null;
/* schools I teach for, seen from my own Plume */
export const mySchools=()=>(!schoolView()&&TEAM.orgs)?TEAM.orgs.filter(o=>o.role==='tutor'&&o.org_id!==CLOUD.org):[];
export const schoolName=()=>String((S.settings&&S.settings.biz)||'').trim()||(ctx()&&ctx().org_name)||'';

/* ---------- teacher filter for calendar, students and invoices (owners/admins of a school) ---------- */
export const TF={id:'all'};
export function teacherFilter(){
  if(!schoolView())return 'all';
  if(TF.id!=='all'&&TF.id!=='none'&&TEAM.members&&!isTeacher(TF.id))TF.id='all';
  return TF.id;
}
export const matchTeacher=uid=>{const f=teacherFilter();return f==='all'||(f==='none'?!isTeacher(uid):uid===f)};

/* ---------- server calls ---------- */
async function rpc(name,body){const r=await sbRest('rpc/'+name,{method:'POST',body:JSON.stringify(body||{})});const x=await r.text();return x?JSON.parse(x):null}
const reason=e=>{const m=String(e&&e.message||e);const k=(m.match(/no_seats|already_member|invalid_email|forbidden|is_owner|wrong_email|invite_expired|invite_used|invalid_invite|team_owner/)||[])[0];return k?t('team.e.'+k):t('plan.err')};
export const teamError=reason;

let lastLoad=0,lastSig='';
export async function loadTeam(){
  if(!loggedIn()||!ctx())return false;
  try{
    const mg=isManager();
    const [orgs,members,invites,terms,tinv]=await Promise.all([rpc('my_orgs'),rpc('team_members'),mg?rpc('team_invites'):Promise.resolve([]),
      mg?rpc('team_terms').catch(()=>[]):Promise.resolve([]),
      mg?sbRest('tutor_invoices?select=*&org_id=eq.'+CLOUD.org+'&order=created_at.desc').then(r=>r.json()).catch(()=>[]):Promise.resolve([])]);
    TEAM.orgs=orgs||[];TEAM.members=members||[];TEAM.invites=invites||[];TEAM.terms=Array.isArray(terms)?terms:[];TEAM.tinv=Array.isArray(tinv)?tinv:[];
    lastLoad=Date.now();const sig=JSON.stringify([TEAM.orgs,TEAM.members,TEAM.invites,TEAM.terms,TEAM.tinv]);const changed=sig!==lastSig;lastSig=sig;
    try{localStorage.setItem('plume.team',JSON.stringify({uid:CLOUD.auth.uid,org:(ctx()||{}).org_id||null,team:{orgs:TEAM.orgs,members:TEAM.members,invites:TEAM.invites,terms:TEAM.terms,tinv:TEAM.tinv}}))}catch(e){}
    return changed;
  }catch(e){console.warn('team',e);return false}
}
/* after a restart: the last known team (teachers, invitations …) until the fresh one arrives */
export function restoreTeamCache(){
  try{const c=JSON.parse(localStorage.getItem('plume.team')||'null');
    if(!c||!c.team||!CLOUD.auth||c.uid!==CLOUD.auth.uid||!ctx()||c.org!==ctx().org_id||TEAM.members)return;
    Object.assign(TEAM,c.team);lastSig=JSON.stringify([TEAM.orgs,TEAM.members,TEAM.invites,TEAM.terms,TEAM.tinv]);
  }catch(e){}
}
/* someone accepted an invitation or left: refresh the lists when the app comes back into view */
export function refreshTeam(force){
  if(!loggedIn()||!ctx()||!inTeam())return;
  if(!force&&Date.now()-lastLoad<20000)return;
  loadTeam().then(changed=>{
    if(!changed)return;applyRoleFlags();
    const a=document.activeElement;const typing=a&&a.closest&&a.closest('#view input,#view textarea,#view select');
    if(!typing&&['team','home','calendar','students','student','invoices','settings'].includes(document.documentElement.dataset.view))render();
  });
}
let watching=false;
function watchTeam(){
  if(watching)return;watching=true;
  document.addEventListener('visibilitychange',()=>{if(!document.hidden)refreshTeam()});
  window.addEventListener('focus',()=>refreshTeam());
  setInterval(()=>{if(!document.hidden)refreshTeam()},60000);
}
export async function invite(email,roleName){
  if(Date.now()>CLOUD.auth.exp)await sbRefresh();
  const r=await fetch(sbUrl('/functions/v1/notify/invite'),{method:'POST',headers:{apikey:CLOUD.cfg.key,Authorization:'Bearer '+CLOUD.auth.at,'Content-Type':'application/json'},body:JSON.stringify({email,role:roleName})});
  if(r.status===404){ // reminder service not updated yet: create the invitation directly, share the link by hand
    const d=await rpc('create_invite',{p_email:email,p_role:roleName});
    return {email:d.email,link:location.origin+'/join?token='+d.token,emailed:false,emailError:'service_old'};
  }
  const d=await r.json().catch(()=>({}));
  if(!r.ok)throw new Error(d.error||('HTTP '+r.status));
  /* the link always opens the app it was created in (the email uses the live address) */
  try{const tok=new URL(d.link).searchParams.get('token');if(tok)d.link=location.origin+'/join?token='+tok}catch(e){}
  if(isOwner()&&S.settings&&!S.settings.school){S.settings.school=true;import('../state.js').then(m=>m.persist())}
  return d;
}
export const revokeInvite=id=>rpc('revoke_invite',{p_id:id});
export const setRole=(uid,r)=>rpc('set_member_role',{p_user:uid,p_role:r});
export const removeMember=uid=>rpc('remove_member',{p_user:uid});
const forgetLocal=()=>{try{['plume.v1','plume.rev','plume.dirty','plume.ctx','plume.team'].forEach(k=>localStorage.removeItem(k))}catch(e){}};
/* switching workspace: save what's pending here first, then load the other one fresh */
export async function switchOrg(org){
  if(CLOUD.dirty){await cloudPush();if(CLOUD.dirty)throw new Error(t('team.e.unsaved'))}
  await rpc('switch_org',{p_org:org});forgetLocal();location.replace('/');
}
export async function inviteInfo(token){
  const r=await fetch(sbUrl('/rest/v1/rpc/invite_info'),{method:'POST',headers:{apikey:CLOUD.cfg.key,Authorization:'Bearer '+CLOUD.cfg.key,'Content-Type':'application/json'},body:JSON.stringify({p_token:token})});
  return r.ok?await r.json():null;
}

/* ---------- joining a school by invitation link: /join?token=… ---------- */
const JK='plume.join';
export function captureJoin(){
  try{
    const q=new URLSearchParams(location.search);const tok=q.get('token');
    if(location.pathname.replace(/\/$/,'')==='/join'&&tok){sessionStorage.setItem(JK,tok);history.replaceState(null,'','/signup')}
  }catch(e){}
}
export const pendingJoin=()=>{try{return sessionStorage.getItem(JK)}catch(e){return null}};
const clearJoin=()=>{try{sessionStorage.removeItem(JK)}catch(e){}};
/* before sign-in: the sign-up page greets the invited teacher and fills in their address */
export async function joinNotice(){
  const tok=pendingJoin();if(!tok)return null;
  const i=await inviteInfo(tok).catch(()=>null);
  if(!i)return null;
  if(i.accepted){clearJoin();return {email:i.email,notice:t('team.alreadyIn',{org:i.org_name||''})}}
  if(!i.valid){clearJoin();return {notice:t('team.e.invite_expired')}}
  return {join:true,email:i.email,org:i.org_name||'',inviter:i.inviter||'',hasAccount:typeof i.has_account==='boolean'?i.has_account:null};
}
/* after sign-in: accept and open the school's workspace */
export async function runJoin(){
  const tok=pendingJoin();if(!tok||!loggedIn())return false;
  try{
    if(CLOUD.dirty&&CLOUD.org)await cloudPush();
    const info=await inviteInfo(tok).catch(()=>null);
    const home=await ownPractice().catch(()=>null);      // someone who already teaches with Plume
    await rpc('accept_invite',{p_token:tok});clearJoin();
    if(home)await rpc('switch_org',{p_org:home}).catch(()=>{});   // …keeps their own workspace; the school is one tap away
    forgetLocal();
    sessionStorage.setItem('plume.joined',JSON.stringify({org:(info&&info.org_name)||'',stayed:!!home}));location.replace('/');return true;
  }catch(e){
    if(/wrong_email/.test(String(e&&e.message||e))){wrongAccount(tok);return false}
    clearJoin();
    const b=sheet(`<div class="sheethead"><h2>${t('team.joinFail')}</h2><button class="iconbtn" data-x aria-label="${esc(t('c.close'))}">${IC.x}</button></div>
      <div class="sheetbody"><p>${esc(reason(e))}</p></div><div class="sheetfoot"><div class="left"></div><button class="btn primary" data-x>${t('c.close')}</button></div>`);
    b.querySelectorAll('[data-x]').forEach(x=>x.onclick=()=>closeSheet(b));
    return false;
  }
}
/* the person's own workspace, if it already has students in it */
async function ownPractice(){
  const orgs=await rpc('my_orgs');const own=(orgs||[]).find(o=>o.role==='owner');
  return own?own.org_id:null;
}
/* signed in with another account than the invited one: switch accounts instead of a dead end */
async function wrongAccount(tok){
  const i=await inviteInfo(tok).catch(()=>null);
  const b=sheet(`<div class="sheethead"><h2>${t('team.wrongTitle')}</h2><button class="iconbtn" data-x aria-label="${esc(t('c.close'))}">${IC.x}</button></div>
    <div class="sheetbody"><p>${esc(t('team.wrongText',{org:(i&&i.org_name)||'',invited:(i&&i.email)||'',me:(CLOUD.auth&&CLOUD.auth.email)||''}))}</p></div>
    <div class="sheetfoot"><div class="left"></div><button class="btn" data-x>${t('c.cancel')}</button><button class="btn primary" data-sw>${t('team.wrongSwitch')}</button></div>`);
  b.querySelectorAll('[data-x]').forEach(x=>x.onclick=()=>{clearJoin();closeSheet(b)});
  b.querySelector('[data-sw]').onclick=()=>{closeSheet(b);import('../cloud/auth.js').then(m=>m.cloudSignOut())};
}
/* tell a teacher that the school gave them students: push to the phone and an email */
export async function notifyAssigned(ids,uid){
  try{
    if(!ids||!ids.length||!uid||!loggedIn()||uid===CLOUD.auth.uid)return;
    await cloudPush().catch(()=>{});
    for(let i=0;i<25&&(CLOUD.pushing||CLOUD.dirty);i++)await new Promise(r=>setTimeout(r,300));
    if(Date.now()>CLOUD.auth.exp)await sbRefresh();
    await fetch(sbUrl('/functions/v1/notify/assigned'),{method:'POST',headers:{apikey:CLOUD.cfg.key,Authorization:'Bearer '+CLOUD.auth.at,'Content-Type':'application/json'},body:JSON.stringify({students:ids,tutor:uid})});
  }catch(e){console.warn('notify assigned',e)}
}
export const leaveSchool=org=>rpc('leave_school',{p_org:org});
export function joinedToast(){try{const raw=sessionStorage.getItem('plume.joined');if(raw){sessionStorage.removeItem('plume.joined');const c=ctx()||{};let j={};try{j=JSON.parse(raw)||{}}catch(e){}
  const org=j.org||c.org_name||'';
  if(j.stayed){const b=sheet(`<div class="sheethead"><h2>${esc(t('team.joinedTitle',{org}))}</h2></div><div class="sheetbody"><p>${esc(t('team.stayedText',{org}))}</p></div><div class="sheetfoot"><div class="left"></div><button class="btn primary" data-x>${t('team.gotIt')}</button></div>`);b.querySelector('[data-x]').onclick=()=>closeSheet(b)}
  else if(c.orgs>1){const b=sheet(`<div class="sheethead"><h2>${esc(t('team.joinedTitle',{org}))}</h2></div><div class="sheetbody"><p>${esc(t('team.joinedText',{role:t('team.role.'+(c.role||'tutor'))}))}</p></div><div class="sheetfoot"><div class="left"></div><button class="btn primary" data-x>${t('team.gotIt')}</button></div>`);b.querySelector('[data-x]').onclick=()=>closeSheet(b)}
  else toast(t('team.joined',{org}))}}catch(e){}}

/* role flags for CSS (hide invoices for tutors, show the team view for managers)
   and the workspace button at the top of the sidebar / phone header, like Slack or Notion */
export function applyRoleFlags(){
  const h=document.documentElement;
  const on=loggedIn()&&!!ctx();
  h.dataset.role=on?role():'owner';
  h.dataset.team=on&&inTeam()&&isManager()?'1':'0';
  const cx=on?ctx():null;h.dataset.parents=cx&&Array.isArray(cx.addons)&&cx.addons.includes('parents')?'1':'0';   // Elternportal booked
  const ws=on&&(inTeam()||(TEAM.orgs&&TEAM.orgs.filter(o=>o.role!=='tutor').length>1));   // teachers keep one Plume
  h.dataset.ws=ws?'1':'0';
  if(ws)document.querySelectorAll('[data-wsbtn]').forEach(b=>{
    const name=schoolName()||t('team.title');const many=TEAM.orgs&&TEAM.orgs.length>1;
    const sub=inTeam()?t('team.role.'+role()):t('ws.personal');
    const html=`<span class="ws-av">${esc(name.slice(0,1).toUpperCase())}</span><span class="ws-txt"><b>${esc(name)}</b><small>${esc(sub)}</small></span>${many||isManager()||inTeam()?'<svg class="ws-chev" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 10l4-4 4 4M8 14l4 4 4-4"/></svg>':''}`;
    if(b.dataset.html!==html){b.innerHTML=html;b.dataset.html=html}
    b.setAttribute('aria-label',t('ws.menu',{name}));b.setAttribute('aria-haspopup','menu');
  });
}
export function afterTeamLoad(){watchTeam();loadTeam().then(()=>{applyRoleFlags();if(['team','settings','students','student','home','calendar','invoices'].includes(document.documentElement.dataset.view))render()})}
